import type { FC, ReactElement, ReactNode } from 'react';
import clsx from 'clsx';

import type { BaseContentProps } from '../Content';

import {
  richContentContainer,
  richContentIconWrapper,
  richContentTrailingWrapper,
  richContentWrapper,
} from './styles.css';

export interface RichContentProps extends BaseContentProps {
  /**
   * The content to wrap.
   */
  children: ReactElement<BaseContentProps>;

  /**
   * Optional leading icon to display before the content
   */
  iconLeading?: ReactNode;

  /**
   * Optional trailing element to display after the content
   */
  iconTrailing?: ReactNode;
}

/**
 * Rich content component that wraps other Content components and adds decorative elements.
 *
 * @example
 * ```tsx
 * // Text with leading icon
 * <Cell kind="regular" size="default">
 *   <RichContent iconLeading={<IconSolidMenuAlt_2 size="16" />}>
 *     <TextContent text="Trace ID" variant="strong" />
 *   </RichContent>
 * </Cell>
 *
 * // Text with trailing tag
 * <Cell kind="regular" size="default">
 *   <RichContent iconTrailing={<Tag kind="success" size="small">LLM</Tag>}>
 *     <TextContent text="Span Name" />
 *   </RichContent>
 * </Cell>
 *
 * // Text with both leading icon and trailing tag
 * <Cell kind="regular" size="default">
 *   <RichContent
 *     iconLeading={<Badge variant="outlineGray" />}
 *     iconTrailing={<Tag kind="success">Active</Tag>}
 *   >
 *     <TextContent text="Status" />
 *   </RichContent>
 * </Cell>
 *
 * // Just content (no decorations)
 * <Cell kind="regular" size="default">
 *   <RichContent>
 *     <TextContent text="Regular text" />
 *   </RichContent>
 * </Cell>
 * ```
 */
export const RichContent: FC<RichContentProps> = ({ children, iconLeading, iconTrailing, className }) => {
  // If no decorations, just pass through
  if (!iconLeading && !iconTrailing) {
    return <>{children}</>;
  }

  return (
    <div className={clsx(richContentContainer, className)}>
      {iconLeading && <div className={richContentIconWrapper}>{iconLeading}</div>}
      <div className={richContentWrapper}>{children}</div>
      {iconTrailing && <div className={richContentTrailingWrapper}>{iconTrailing}</div>}
    </div>
  );
};
